"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, MapPin, UserCheck, ArrowRight, Search, Layers, Code as Code2, TrendingUp, ShieldCheck, Clock, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function ProcessCoverage() {
  const shouldReduceMotion = useReducedMotion();
  const steps = [{
    step: "01",
    title: "Discovery and technical baseline",
    description: "I personally evaluate your website architecture, indexation status, crawl health, and search footprint to identify what is holding your search visibility back.",
    icon: Search,
    tag: "Deep analysis"
  }, {
    step: "02",
    title: "Commercial search architecture",
    description: "I map your customer search intent to clean URL structures and high-intent topic clusters, helping your key services compete for visibility where purchasing decisions happen.",
    icon: Layers,
    tag: "Targeted strategy"
  }, {
    step: "03",
    title: "Practical execution and content",
    description: "I implement technical fixes, optimize schema, refine on-page copy, and improve internal linking directly. No hypothetical decks or tasks pushed back to you.",
    icon: Code2,
    tag: "Direct implementation"
  }, {
    step: "04",
    title: "Transparent measurement and refinement",
    description: "I track qualified impressions, organic conversions, and query positions with straightforward reporting. We adjust tactics based on real search trends.",
    icon: TrendingUp,
    tag: "Honest metrics"
  }];
  const directModelBenefits = [{
    title: "Direct consultant access",
    description: "Every email, strategy session, and recommendation comes directly from me, providing focused senior expertise across every stage of your project.",
    icon: UserCheck
  }, {
    title: "Dedicated strategic investment",
    description: "Your budget goes entirely into expert SEO strategy and tangible execution designed to build lasting organic search performance.",
    icon: ShieldCheck
  }, {
    title: "Fast communication and clarity",
    description: "When questions arise or your priorities shift, you speak directly with the consultant working on your site for prompt answers and clear guidance.",
    icon: Clock
  }];
  const featuredLocations = [{
    name: "Swindon & Wiltshire",
    to: "/ContactPage",
    note: "Consultancy Base"
  }, {
    name: "Milton Keynes",
    to: "/LocalSeoConsultantMiltonKeynesPage",
    note: "Regional Focus"
  }, {
    name: "Northampton",
    to: "/LocalSeoConsultantNorthamptonPage",
    note: "Regional Focus"
  }, {
    name: "Peterborough",
    to: "/LocalSeoConsultantPeterboroughPage",
    note: "Regional Focus"
  }, {
    name: "Norwich",
    to: "/LocalSeoConsultantNorwichPage",
    note: "Regional Focus"
  }, {
    name: "York",
    to: "/LocalSeoConsultantYorkPage",
    note: "Regional Focus"
  }, {
    name: "Wakefield",
    to: "/LocalSeoConsultantWakefieldPage",
    note: "Regional Focus"
  }, {
    name: "Cheltenham",
    to: "/LocalSeoConsultantCheltenhamPage",
    note: "Regional Focus"
  }];
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="processandcoverage" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider text-primary border-primary/20">
            Working model and UK reach
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
            SEO process and UK coverage
          </h2>
          <p className="mt-4 text-lg text-muted-foreground font-sans leading-relaxed">
            A practical four-stage optimization framework paired with hands-on consultant support for businesses nationwide across the UK.
          </p>
        </div>

        {/* 4-Step Methodology Grid */}
        <div className="mb-20 lg:mb-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <span className="text-sm font-semibold text-primary uppercase tracking-wider font-sans">
                My four-stage methodology
              </span>
              <h3 className="text-2xl font-bold font-default text-foreground mt-1">
                A disciplined, practical SEO framework
              </h3>
            </div>
            <Button asChild variant="outline" size="sm" className="w-fit">
              <Link to="/SeoAuditsPage">
                See audit process <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((item, i) => {
            const IconComponent = item.icon;
            return <motion.div key={item.step} variants={itemVariants} data-index={i} className="h-full">
                  <Card className="h-full border border-border/80 bg-card rounded-xl shadow-xs hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-200">
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="flex items-center justify-between mb-5">
                        <span className="text-2xl font-bold text-primary/70 font-mono tracking-tight">
                          {item.step}
                        </span>
                        <div className="p-2.5 rounded-lg bg-secondary text-primary">
                          <IconComponent className="size-5" />
                        </div>
                      </div>

                      <Badge variant="outline" className="w-fit text-2xs mb-3 font-normal border-border">
                        {item.tag}
                      </Badge>

                      <h4 className="text-lg font-bold font-default text-foreground mb-3 leading-snug">
                        {item.title}
                      </h4>

                      <p className="text-sm text-muted-foreground font-sans leading-relaxed flex-grow">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Direct Working Model & UK Coverage Split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Direct Access Philosophy */}
          <div className="lg:col-span-7 space-y-6">
            <div className="p-8 rounded-2xl bg-card border border-border/80 shadow-sm">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/80 text-primary text-xs font-semibold mb-6">
                <UserCheck className="size-3.5" />
                Senior consultant assurance
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold font-default text-foreground tracking-tight mb-4">
                You work directly with me
              </h3>
              <p className="text-muted-foreground font-sans text-base leading-relaxed mb-8">
                As an independent consultant, I handle every audit, strategy review, and technical implementation myself. You work directly with me from day one—with clear deliverables, transparent communication, and a single point of contact throughout.
              </p>

              <div className="space-y-5">
                {directModelBenefits.map((benefit, i) => {
                const BenefitIcon = benefit.icon;
                return <div key={benefit.title} data-index={i} className="flex items-start gap-4">
                      <div className="mt-1 p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                        <BenefitIcon className="size-5" />
                      </div>
                      <div>
                        <h4 className="text-base font-semibold font-default text-foreground">
                          {benefit.title}
                        </h4>
                        <p className="text-sm text-muted-foreground font-sans mt-1 leading-relaxed">
                          {benefit.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>

              <div className="mt-8 pt-6 border-t border-border flex flex-wrap gap-4 items-center justify-between">
                <div className="text-sm text-muted-foreground font-sans">
                  Based at Windmill Hill Business Park, Swindon SN5 6QR
                </div>
                <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/ContactPage">
                    <PhoneCall className="size-4 mr-1.5" /> Speak with Yassine
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: UK Coverage Breakdown */}
          <div className="lg:col-span-5 space-y-6">
            <div className="p-8 rounded-2xl bg-card border border-border/80 shadow-sm">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/80 text-primary text-xs font-semibold mb-6">
                <MapPin className="size-3.5" />
                United Kingdom reach
              </div>

              <h3 className="text-xl sm:text-2xl font-bold font-default text-foreground tracking-tight mb-3">
                National and regional coverage
              </h3>
              <p className="text-sm text-muted-foreground font-sans leading-relaxed mb-6">
                Whether you need nationwide competitive visibility or focused local footfall, I build tailored strategies mapped to your target geographical audience.
              </p>

              {/* Service scope links */}
              <div className="grid grid-cols-2 gap-3 mb-6 [&>*:last-child]:col-span-2">
                <Button asChild variant="outline" size="sm" className="justify-start text-xs font-medium h-auto py-2.5 px-3 border-border bg-background">
                  <Link to="/NationalSeoPage">
                    <CheckCircle2 className="size-3.5 text-primary mr-1.5 shrink-0" />
                    National SEO
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="justify-start text-xs font-medium h-auto py-2.5 px-3 border-border bg-background">
                  <Link to="/LocalSEOPage">
                    <CheckCircle2 className="size-3.5 text-primary mr-1.5 shrink-0" />
                    Local SEO
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="justify-start text-xs font-medium h-auto py-2.5 px-3 border-border bg-background">
                  <Link to="/B2BSeoLeadGenerationPage">
                    <CheckCircle2 className="size-3.5 text-primary mr-1.5 shrink-0" />
                    B2B Lead Gen
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="justify-start text-xs font-medium h-auto py-2.5 px-3 border-border bg-background">
                  <Link to="/EcommerceSeoPage">
                    <CheckCircle2 className="size-3.5 text-primary mr-1.5 shrink-0" />
                    Ecommerce SEO
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="justify-start text-xs font-medium h-auto py-2.5 px-3 border-border bg-background">
                  <Link to="/B2BProfessionalServicesPage">
                    <CheckCircle2 className="size-3.5 text-primary mr-1.5 shrink-0" />
                    B2B & Professional Services
                  </Link>
                </Button>
              </div>

              {/* Location pill directory */}
              <div className="border-t border-border/70 pt-5">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 font-sans">
                  Key location hubs served
                </div>
                <div className="flex flex-wrap gap-2">
                  {featuredLocations.map((loc, i) => <div key={loc.name} data-index={i}>
                      <Link to={loc.to} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-muted/60 hover:bg-secondary text-xs text-foreground font-sans transition-colors border border-border/60">
                        <MapPin className="size-3 text-primary shrink-0" />
                        <span>{loc.name}</span>
                      </Link>
                    </div>)}
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-border/60">
                <p className="text-xs text-muted-foreground font-sans leading-normal">
                  Remote consulting available nationwide across England, Scotland, Wales, and Northern Ireland with on-site discovery sessions upon request.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}